import React, { useState, useEffect, useRef, createRef } from 'react';
import { useAppSelector } from 'store/hooks';

import { currentLessonSelector } from 'store/lesson/selectors';

import { LINE_COLORS, ITEM_TYPES } from './ConnectionQuizPreviewPage.constants';

import {
  shuffle,
  getIntersectionArea,
  shuffleIncorrectAnswers,
} from './ConnectionQuizPreviewPage.helpers';

import ConnectionQuizPreviewPage from './ConnectionQuizPreviewPage';

import type { ItemClickPayload } from './components/Item/Item';

type LessonAnswer = {
  id: string;
  leftContent: string;
  rightContent: string;
};

type QuizLesson = {
  answers: LessonAnswer[];
};

type QuizBoardItem = {
  id: string;
  content: string;
  type: typeof ITEM_TYPES.LEFT_ITEM | typeof ITEM_TYPES.RIGHT_ITEM;
};

type LinePoint = { x: number; y: number };

type LineRecord = Record<string, unknown> & {
  startId?: string;
  startType?: string;
  finishId?: string;
  finishType?: string;
  color?: string;
  point1?: LinePoint;
  point2?: LinePoint;
  point3?: LinePoint;
  point4?: LinePoint;
};

type StartLinePosition = {
  x?: number;
  y?: number;
  startId?: string | null;
  startType?: string;
};

const ConnectionQuizPreviewPageContainer = () => {
  const leftItemsRef = useRef<Array<React.RefObject<HTMLDivElement | null>>>(
    [],
  );
  const rightItemsRef = useRef<Array<React.RefObject<HTMLDivElement | null>>>(
    [],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const lesson = useAppSelector(currentLessonSelector) as QuizLesson;

  const [lineDrawing, setLineDrawing] = useState(false);
  const [linesValidated, setLinesValidated] = useState(false);
  const [rightItems, setRightItems] = useState<QuizBoardItem[]>([]);
  const [leftItems, setLeftItems] = useState<QuizBoardItem[]>([]);
  const [lines, setLines] = useState<LineRecord[]>([]);
  const [correctLinesIds, setCorrectLinesIds] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [previewType, setPreviewType] = useState('Plugin');
  const [isTestMode, setTestMode] = useState(false);
  const [startLinePosition, setStartLinePosition] = useState<StartLinePosition>(
    {
      x: 0,
      y: 0,
      startId: null,
      startType: '',
    },
  );

  useEffect(() => {
    if (lesson.answers.length) {
      const shuffledLeftItems = shuffle(
        lesson.answers.map((answer: any) => ({
          id: answer.id,
          content: answer.leftContent,
          type: ITEM_TYPES.LEFT_ITEM,
        })),
      ) as QuizBoardItem[];

      const shuffledRightItems = shuffle(
        lesson.answers.map((answer: any) => ({
          id: answer.id,
          content: answer.rightContent,
          type: ITEM_TYPES.RIGHT_ITEM,
        })),
      ) as QuizBoardItem[];

      leftItemsRef.current = Array.from(Array(shuffledLeftItems.length)).map(
        () => createRef<HTMLDivElement>(),
      );

      rightItemsRef.current = Array.from(Array(shuffledRightItems.length)).map(
        () => createRef<HTMLDivElement>(),
      );

      setLeftItems(shuffledLeftItems);
      setRightItems(shuffledRightItems);
    }
  }, [lesson.answers]);

  useEffect(() => {
    if (
      lines.length === lesson.answers.length &&
      !lineDrawing &&
      lines.length > 0
    ) {
      checkAnswers();
      checkLines();

      setLinesValidated(true);
    }
  }, [lineDrawing]);

  const startLineDrawing = ({
    id,
    pointRef,
    type,
  }: {
    event: React.MouseEvent;
    id: string;
    pointRef: React.RefObject<HTMLDivElement | null>;
    type: ItemClickPayload['type'];
    pointUsed?: boolean;
  }) => {
    const root = rootRef.current;
    const container = containerRef.current;
    const pointEl = pointRef.current;

    if (!root || !container || !pointEl) {
      return;
    }

    const pagePosition = root.getBoundingClientRect();
    const elementPosition = pointEl.getBoundingClientRect();

    const canAddLine = lines.length === lesson.answers.length && !lineDrawing;

    if (!canAddLine) {
      setLineDrawing(true);

      setStartLinePosition({
        x:
          elementPosition.left +
          elementPosition.width -
          container.offsetLeft -
          pagePosition.x,
        y:
          elementPosition.top +
          elementPosition.height / 2 -
          container.offsetTop -
          pagePosition.y,
        startId: id,
        startType: type,
      });
    }
  };

  const handleProcessDrawing = (event: React.MouseEvent) => {
    const leftItersectionArea = getIntersectionArea(
      leftItemsRef.current,
      event,
    );

    const rightItersectionArea = getIntersectionArea(
      rightItemsRef.current,
      event,
    );

    const currentIntersectionArea = leftItersectionArea || rightItersectionArea;

    let point4X: number | undefined;
    let point4Y: number | undefined;

    const root = rootRef.current;
    const container = containerRef.current;

    if (!root || !container) {
      return;
    }

    const pagePosition = root.getBoundingClientRect();

    if (currentIntersectionArea) {
      point4X =
        currentIntersectionArea.left +
        currentIntersectionArea.diameter / 2 -
        root.offsetLeft;
      point4Y =
        currentIntersectionArea.top +
        currentIntersectionArea.diameter / 2 -
        container.offsetTop -
        pagePosition.y;
    } else {
      point4X = event.clientX - root.offsetLeft;
      point4Y = event.clientY - container.offsetTop - pagePosition.y;
    }

    const nextLines = [...lines];

    const lineLength = (point4X - (startLinePosition.x ?? 0)) / 2;

    const point2X = (startLinePosition.x ?? 0) + lineLength;

    const point3X = event.clientX - lineLength - root.offsetLeft;

    nextLines[currentLineIndex] = {
      startId: startLinePosition.startId,
      startType: startLinePosition.startType,
      point1: { x: startLinePosition.x ?? 0, y: startLinePosition.y ?? 0 },
      point2: {
        x: point2X,
        y: startLinePosition.y ?? 0,
      },
      point3: {
        x: point3X,
        y: event.clientY - container.offsetTop - pagePosition.y,
      },
      point4: {
        x: point4X,
        y: point4Y,
      },
    };

    setLines(nextLines);
  };

  const finishLineDrawing = ({
    id,
    pointRef,
    type,
  }: {
    event: React.MouseEvent;
    id: string;
    pointRef: React.RefObject<HTMLDivElement | null>;
    type: ItemClickPayload['type'];
  }) => {
    const root = rootRef.current;
    const container = containerRef.current;
    const pointEl = pointRef.current;

    if (!root || !container || !pointEl) {
      return;
    }

    const pagePosition = root.getBoundingClientRect();

    const elementBox = pointEl.getBoundingClientRect();

    const nextLines = [...lines];

    const point4X = elementBox.left + elementBox.width / 2 - root.offsetLeft;

    nextLines[currentLineIndex] = {
      ...nextLines[currentLineIndex],
      finishId: id,
      finishType: type,
      color: LINE_COLORS.ROYAL_BLUE_VIOLET,
      point4: {
        x: point4X,
        y:
          elementBox.top +
          elementBox.height / 2 -
          container.offsetTop -
          pagePosition.y,
      },
    };

    setLines(nextLines);
    setLineDrawing(false);
    setCurrentLineIndex(currentLineIndex + 1);
    setStartLinePosition({});
  };

  const checkAnswers = () => {
    setCorrectLinesIds(
      lines
        .filter((item: any) => item.startId === item.finishId)
        .map((item: any) => item.startId as string),
    );
  };

  const checkLines = () => {
    const checkedItems = lines.map((item: any) => {
      if (item.startId === item.finishId) {
        return { ...item, color: LINE_COLORS.SHAMROCK };
      } else {
        return { ...item, color: LINE_COLORS.RADIACAL_RED };
      }
    });

    setLines(checkedItems);
  };

  const deleteLine = (id: string, type: string) => {
    if (lines.length > 0 && !linesValidated) {
      const nextLines = [...lines];
      const updatedLines = nextLines.filter(
        (line: any) =>
          !(
            (line.startId === id && line.startType === type) ||
            (line.finishId === id && line.finishType === type)
          ),
      );

      setLines(updatedLines);
      setCurrentLineIndex(currentLineIndex - 1);
    }
  };

  const handleDeleteIncorrectLines = () => {
    const nextLines = [...lines];

    const updatedLines = nextLines
      .filter((line: any) => line.startId === line.finishId)
      .map((line: any) => ({ ...line, color: LINE_COLORS.ROYAL_BLUE_VIOLET }));

    setLines(updatedLines);

    const nextLeftItems = shuffleIncorrectAnswers(leftItems, updatedLines);
    const nextRightItems = shuffleIncorrectAnswers(rightItems, updatedLines);

    setLeftItems(nextLeftItems as QuizBoardItem[]);
    setRightItems(nextRightItems as QuizBoardItem[]);

    setCurrentLineIndex(
      currentLineIndex - nextLines.length + updatedLines.length,
    );

    setLinesValidated(false);
  };

  const handleItemClick = (payload: ItemClickPayload) => {
    const { event, id, pointRef, type, pointUsed } = payload;
    if (startLinePosition.startType === type) {
      return;
    }

    const canDeleteLine = lines.some(
      (line: any) =>
        (line.startId === id && line.startType === type) ||
        (line.finishId === id && line.finishType === type),
    );

    if (canDeleteLine) {
      deleteLine(id, type);
    }

    if (lineDrawing && !pointUsed) {
      finishLineDrawing({ event, id, pointRef, type });
    } else {
      startLineDrawing({ event, id, pointRef, type, pointUsed });
    }
  };

  const handleRestartLessonClick = () => {
    setLinesValidated(false);
    setLines([]);
    setCorrectLinesIds([]);
    setCurrentLineIndex(0);
  };

  const handleChangePreviewType = (type: string) => {
    setPreviewType(type);
    handleRestartLessonClick();
  };

  return (
    <ConnectionQuizPreviewPage
      lineDrawing={lineDrawing}
      linesValidated={linesValidated}
      containerRef={containerRef}
      leftItemsRef={leftItemsRef}
      rightItemsRef={rightItemsRef}
      rootRef={rootRef}
      leftItems={leftItems}
      rightItems={rightItems}
      lines={lines}
      correctLinesIds={correctLinesIds}
      lesson={lesson}
      startLinePosition={startLinePosition}
      onProcessDrawing={handleProcessDrawing}
      onItemClick={handleItemClick}
      onTryAgainClick={handleDeleteIncorrectLines}
      onRestartLessonClick={handleRestartLessonClick}
      previewType={previewType}
      setPreviewType={setPreviewType}
      isTestMode={isTestMode}
      setTestMode={setTestMode}
      onChangePreviewType={handleChangePreviewType}
    />
  );
};

export default React.memo(ConnectionQuizPreviewPageContainer);
